import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  createAppOrganizerCommunity,
  getAppCommunities,
  getAppOrganizers,
  reassignAppCommunity,
} from '../services/api';

const DEFAULT_RULES = [
  'Respect all players',
  'No abusive language or behavior',
  'Arrive 15 mins before match',
  'Payment required before match',
  'No-shows may be removed',
].join('\n');

const EMPTY = {
  organizerId:'', name:'', shortDescription:'', location:'', address:'', city:'',
  visibility:'public', rules:DEFAULT_RULES,
  minimumAge:'18', gender:'mixed', approvalProcess:'manual', defaultCapacity:'16',
  skillLevels:['casual','amateur','semi_pro'],
};

const control = { width:'100%', minWidth:0 };
const grid = { display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:14 };

const Field = React.memo(function Field({ label, children, wide }) {
  return <div style={wide ? { gridColumn:'1 / -1' } : undefined}>
    <label style={{ display:'block', marginBottom:6, color:'var(--text-dim)', fontSize:12, textTransform:'uppercase' }}>{label}</label>
    {children}
  </div>;
});

const Input = React.memo(function Input({ value, onChange, ...props }) {
  return <input {...props} className="search-input" style={control} value={value} onChange={e => onChange(e.target.value)} />;
});

const Select = React.memo(function Select({ value, onChange, children }) {
  return <select className="search-input" style={control} value={value} onChange={e => onChange(e.target.value)}>{children}</select>;
});

const ImageUpload = React.memo(function ImageUpload({ label, file, onChange, help }) {
  return <Field label={label}>
    <label className="search-input" style={{ ...control, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, cursor:'pointer' }}>
      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:file ? 'var(--text-primary)' : 'var(--text-dim)' }}>
        {file ? file.name : 'Choose image (optional)'}
      </span>
      <span className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:11, flexShrink:0 }}>Browse</span>
      <input type="file" accept="image/*" style={{ display:'none' }} onChange={event => onChange(event.target.files?.[0] || null)} />
    </label>
    <div style={{ marginTop:5, color:'var(--text-dim)', fontSize:10 }}>{help}</div>
  </Field>;
});

function organizerName(row) {
  const user = row.user || {};
  return row.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || row.id;
}

function OrganizerPicker({ organizers, loading, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const selected = organizers.find(row => row.id === value) || null;
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return organizers;
    return organizers.filter(row => `${organizerName(row)} ${row.user?.email || ''} ${row.city || ''}`.toLowerCase().includes(term));
  }, [organizers, search]);

  useEffect(() => {
    if (!open) return undefined;
    const close = event => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = organizerId => {
    onChange(organizerId);
    setOpen(false);
    setSearch('');
  };

  return <div ref={rootRef} style={{ position:'relative' }}>
    <button type="button" className="search-input" disabled={loading} aria-haspopup="listbox" aria-expanded={open}
      onClick={() => setOpen(previous => !previous)}
      style={{ ...control, display:'flex', alignItems:'center', justifyContent:'space-between', textAlign:'left', cursor:loading ? 'default' : 'pointer' }}>
      <span>{loading ? 'Loading organizers…' : selected ? organizerName(selected) : 'Select an organizer'}</span>
      <span aria-hidden="true" style={{ color:'var(--text-dim)' }}>{open ? '▲' : '▼'}</span>
    </button>
    {open && <div role="listbox" style={{ position:'absolute', zIndex:60, top:'calc(100% + 5px)', left:0, right:0, maxHeight:350, overflowY:'auto', padding:8, border:'1px solid var(--border)', borderRadius:8, background:'var(--bg-card)', boxShadow:'0 16px 35px rgba(0,0,0,.35)' }}>
      <input type="search" className="search-input" style={{ ...control, marginBottom:7, position:'sticky', top:0, zIndex:1 }}
        placeholder="Search organizers by name, email or city" value={search} onChange={event => setSearch(event.target.value)} autoFocus />
      {filtered.map(row => <button key={row.id} type="button" role="option" aria-selected={value === row.id} onClick={() => choose(row.id)}
        style={{ width:'100%', padding:'9px 10px', border:0, borderRadius:6, background:value === row.id ? 'rgba(167,244,50,.12)' : 'transparent', color:'var(--text-primary)', textAlign:'left', cursor:'pointer' }}>
        <span style={{ display:'block', fontSize:12 }}>{organizerName(row)}</span>
        <span style={{ display:'block', marginTop:2, fontSize:10, color:'var(--text-dim)' }}>{row.user?.email || row.city || row.id}</span>
      </button>)}
      {!filtered.length && <div style={{ padding:'12px 10px', fontSize:11, color:'var(--text-dim)' }}>No matching organizers found.</div>}
    </div>}
  </div>;
}

function CommunityPicker({ communities, loading, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const selected = communities.find(row => row.id === value) || null;
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return communities;
    return communities.filter(row => `${row.name || ''} ${row.location || ''} ${row.organizerName || organizerName(row.organizer || {})}`.toLowerCase().includes(term));
  }, [communities, search]);

  useEffect(() => {
    if (!open) return undefined;
    const close = event => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = groupId => {
    onChange(groupId);
    setOpen(false);
    setSearch('');
  };

  return <div ref={rootRef} style={{ position:'relative' }}>
    <button type="button" className="search-input" disabled={loading} onClick={() => setOpen(previous => !previous)}
      style={{ ...control, display:'flex', alignItems:'center', justifyContent:'space-between', textAlign:'left', cursor:loading ? 'default' : 'pointer' }}>
      <span>{loading ? 'Loading groups…' : selected ? selected.name : 'Select a group'}</span>
      <span aria-hidden="true" style={{ color:'var(--text-dim)' }}>{open ? '▲' : '▼'}</span>
    </button>
    {open && <div style={{ position:'absolute', zIndex:60, top:'calc(100% + 5px)', left:0, right:0, maxHeight:350, overflowY:'auto', padding:8, border:'1px solid var(--border)', borderRadius:8, background:'var(--bg-card)', boxShadow:'0 16px 35px rgba(0,0,0,.35)' }}>
      <input type="search" className="search-input" style={{ ...control, marginBottom:7, position:'sticky', top:0, zIndex:1 }}
        placeholder="Search groups by name, location or organizer" value={search} onChange={event => setSearch(event.target.value)} autoFocus />
      {filtered.map(row => <button key={row.id} type="button" onClick={() => choose(row.id)}
        style={{ width:'100%', padding:'9px 10px', border:0, borderRadius:6, background:value === row.id ? 'rgba(167,244,50,.12)' : 'transparent', color:'var(--text-primary)', textAlign:'left', cursor:'pointer' }}>
        <span style={{ display:'block', fontSize:12 }}>{row.name}</span>
        <span style={{ display:'block', marginTop:2, fontSize:10, color:'var(--text-dim)' }}>{row.organizerName || organizerName(row.organizer || {})} · {row.location || 'No location'}</span>
      </button>)}
      {!filtered.length && <div style={{ padding:'12px 10px', fontSize:11, color:'var(--text-dim)' }}>No matching groups found.</div>}
    </div>}
  </div>;
}

export default function CommunityCreation({ toast }) {
  const { auth } = useApp();
  const [form, setForm] = useState(EMPTY);
  const [organizers, setOrganizers] = useState([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [logo, setLogo] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(true);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');
  const [destinationOrganizerId, setDestinationOrganizerId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  const update = useCallback((key, value) => setForm(previous => ({ ...previous, [key]:value })), []);
  const loadCommunities = useCallback(async () => {
    setLoadingCommunities(true);
    try {
      const response = await getAppCommunities(auth?.token, { page:1, limit:100 });
      setCommunities(Array.isArray(response.data) ? response.data : []);
    } catch (error) { toast?.(error.message, 'error'); }
    finally { setLoadingCommunities(false); }
  }, [auth?.token, toast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getAppOrganizers(auth?.token, { page:1, limit:100 });
        if (!cancelled) setOrganizers(Array.isArray(response.data) ? response.data : []);
      } catch (error) { if (!cancelled) toast?.(error.message, 'error'); }
      finally { if (!cancelled) setLoadingOrganizers(false); }
    })();
    return () => { cancelled = true; };
  }, [auth?.token, toast]);

  useEffect(() => { loadCommunities(); }, [loadCommunities]);

  const toggleSkill = useCallback(skill => setForm(previous => ({
    ...previous,
    skillLevels: previous.skillLevels.includes(skill)
      ? previous.skillLevels.filter(item => item !== skill)
      : [...previous.skillLevels, skill],
  })), []);

  const submit = async event => {
    event.preventDefault();
    if (!form.organizerId || !form.name.trim() || !form.location.trim()) {
      toast?.('Select an organizer and enter the group name and location.', 'error'); return;
    }
    setSubmitting(true); setCreated(null);
    try {
      const payload = new FormData();
      payload.append('name', form.name.trim());
      payload.append('shortDescription', form.shortDescription.trim());
      payload.append('location', form.location.trim());
      payload.append('address', form.address.trim());
      payload.append('city', form.city.trim());
      payload.append('visibility', form.visibility);
      payload.append('communityRules', JSON.stringify(form.rules.split('\n').map(value => value.trim()).filter(Boolean)));
      payload.append('membershipSettings', JSON.stringify({
          minimumAge:Number(form.minimumAge), skillLevels:form.skillLevels, gender:form.gender,
          approvalProcess:form.approvalProcess, defaultCapacity:Number(form.defaultCapacity),
      }));
      if (logo) payload.append('logo', logo);
      if (coverImage) payload.append('coverImage', coverImage);
      const response = await createAppOrganizerCommunity(auth?.token, form.organizerId, payload);
      setCreated(response.data); toast?.('Community created and assigned successfully.');
      setForm(previous => ({ ...EMPTY, organizerId:previous.organizerId }));
      setLogo(null); setCoverImage(null);
      await loadCommunities();
    } catch (error) { toast?.(error.message, 'error'); }
    finally { setSubmitting(false); }
  };

  const reassign = async event => {
    event.preventDefault();
    const selected = communities.find(row => row.id === selectedCommunityId);
    if (!selectedCommunityId || !destinationOrganizerId) {
      toast?.('Select a group and its new organizer.', 'error'); return;
    }
    if (selected?.organizerId === destinationOrganizerId) {
      toast?.('This group is already assigned to that organizer.', 'error'); return;
    }
    setReassigning(true);
    try {
      await reassignAppCommunity(auth?.token, selectedCommunityId, destinationOrganizerId);
      toast?.('Group reassigned successfully.');
      setSelectedCommunityId('');
      setDestinationOrganizerId('');
      await loadCommunities();
    } catch (error) { toast?.(error.message, 'error'); }
    finally { setReassigning(false); }
  };

  return <div style={{ maxWidth:1100, margin:'0 auto' }}>
    <div style={{ marginBottom:22 }}>
      <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Create Group</h1>
      <p style={{ color:'var(--text-dim)', fontSize:13, margin:'5px 0 0' }}>Create a football community and assign it to an existing organizer.</p>
    </div>
    <form className="card" onSubmit={submit} style={{ padding:24 }}>
      <div className="card-title" style={{ marginBottom:18 }}>Organizer assignment</div>
      <div style={grid}>
        <Field label="Assign to organizer" wide>
          <OrganizerPicker organizers={organizers} loading={loadingOrganizers} value={form.organizerId} onChange={value => update('organizerId', value)} />
        </Field>
      </div>

      <div className="card-title" style={{ margin:'28px 0 18px' }}>Group profile</div>
      <div style={grid}>
        <Field label="Group name"><Input value={form.name} onChange={value => update('name', value)} placeholder="Football Hub" /></Field>
        <Field label="Visibility"><Select value={form.visibility} onChange={value => update('visibility', value)}><option value="public">Public</option><option value="private">Private</option></Select></Field>
        <Field label="Short description" wide><textarea className="search-input" style={{ ...control, minHeight:82, resize:'vertical' }} value={form.shortDescription} onChange={e => update('shortDescription', e.target.value)} /></Field>
        <Field label="Location"><Input value={form.location} onChange={value => update('location', value)} placeholder="Dubai, United Arab Emirates" /></Field>
        <Field label="City"><Input value={form.city} onChange={value => update('city', value)} placeholder="Dubai" /></Field>
        <Field label="Address" wide><Input value={form.address} onChange={value => update('address', value)} placeholder="Full address (optional)" /></Field>
        <ImageUpload label="Group logo" file={logo} onChange={setLogo} help="Optional · uploaded to Firebase community_logos/" />
        <ImageUpload label="Banner / cover image" file={coverImage} onChange={setCoverImage} help="Optional · uploaded to Firebase community_covers/" />
      </div>

      <div className="card-title" style={{ margin:'28px 0 18px' }}>Community rules</div>
      <Field label="One rule per line" wide><textarea className="search-input" style={{ ...control, minHeight:140, resize:'vertical' }} value={form.rules} onChange={e => update('rules', e.target.value)} /></Field>

      <div className="card-title" style={{ margin:'28px 0 18px' }}>Membership settings</div>
      <div style={grid}>
        <Field label="Minimum age"><Input type="number" min="1" max="100" value={form.minimumAge} onChange={value => update('minimumAge', value)} /></Field>
        <Field label="Default capacity"><Input type="number" min="2" max="1000" value={form.defaultCapacity} onChange={value => update('defaultCapacity', value)} /></Field>
        <Field label="Gender"><Select value={form.gender} onChange={value => update('gender', value)}><option value="male">Male</option><option value="female">Female</option><option value="mixed">Mixed</option></Select></Field>
        <Field label="Approval process"><Select value={form.approvalProcess} onChange={value => update('approvalProcess', value)}><option value="automatic">Automatic approval</option><option value="manual">Manual approval</option></Select></Field>
        <Field label="Skill levels" wide><div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>{[['casual','Casual'],['amateur','Amateur'],['semi_pro','Semi-Pro'],['professional','Professional']].map(([id,label]) => <button key={id} type="button" className={`btn ${form.skillLevels.includes(id) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggleSkill(id)}>{label}</button>)}</div></Field>
      </div>

      {created && <div style={{ marginTop:22, padding:14, border:'1px solid rgba(167,244,50,.35)', borderRadius:8, color:'var(--accent)', background:'rgba(167,244,50,.06)' }}>Created “{created.name}” — Group ID: {created.groupId}</div>}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:26 }}><button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create and assign group'}</button></div>
    </form>

    <form className="card" onSubmit={reassign} style={{ padding:24, marginTop:22 }}>
      <div className="card-title" style={{ marginBottom:6 }}>Reassign an existing group</div>
      <p style={{ color:'var(--text-dim)', fontSize:12, margin:'0 0 18px' }}>Transfer management of a group to another existing organizer. The group ID and mobile links will not change.</p>
      <div style={grid}>
        <Field label="Group">
          <CommunityPicker communities={communities} loading={loadingCommunities} value={selectedCommunityId} onChange={setSelectedCommunityId} />
        </Field>
        <Field label="New organizer">
          <OrganizerPicker organizers={organizers} loading={loadingOrganizers} value={destinationOrganizerId} onChange={setDestinationOrganizerId} />
        </Field>
      </div>
      {selectedCommunityId && <div style={{ marginTop:14, padding:12, border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:11 }}>
        Current organizer: <span style={{ color:'var(--text-primary)' }}>{communities.find(row => row.id === selectedCommunityId)?.organizerName || 'Unknown'}</span>
      </div>}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:20 }}>
        <button className="btn btn-primary" type="submit" disabled={reassigning}>{reassigning ? 'Reassigning…' : 'Reassign group'}</button>
      </div>
    </form>

  </div>;
}
